import { Brain, Calendar, Grid3x3, LayoutGrid } from "lucide-react";
import { useState } from "react";
import CardView from "@/bizminer/App/PillarInsights/ImprovementLevers/_components/CardView.tsx";
import GridView from "@/bizminer/App/PillarInsights/ImprovementLevers/_components/GridView.tsx";
import ImprovementLeverSheet from "@/bizminer/App/PillarInsights/ImprovementLevers/_components/ImprovementLeverSheet.tsx";
import RoadmapView from "@/bizminer/App/PillarInsights/ImprovementLevers/_components/RoadmapView.tsx";
import BizminerLayout from "@/bizminer/layouts/app/BizminerLayout.tsx";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  BizminerImprovementLever,
  BizminerPillarInsight,
} from "@/types/serializers";

type ViewType = "cards" | "grid" | "roadmap";

interface ImprovementLeversIndexProps {
  pillar_insight: BizminerPillarInsight;
  improvement_levers: BizminerImprovementLever[];
}

const EmptyMessage = () => {
  return (
    <div className="text-center py-12">
      <Brain className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
      <h3 className="text-lg font-medium text-foreground mb-2">
        No improvement levers yet
      </h3>
      <p className="text-muted-foreground">
        Improvement levers will appear here as they are discovered.
      </p>
    </div>
  );
};

export default function Index({
  pillar_insight,
  improvement_levers,
}: ImprovementLeversIndexProps) {
  const [view, setView] = useState<ViewType>("cards");
  const [selectedLever, setSelectedLever] =
    useState<BizminerImprovementLever | null>(null);

  const handleViewChange = (newView: ViewType) => {
    setView(newView);
  };

  const handleSelect = (lever: BizminerImprovementLever) => {
    setSelectedLever(lever);
  };

  const handleClose = () => {
    setSelectedLever(null);
  };

  return (
    <BizminerLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex size-12 items-center justify-center rounded-lg border border-border/50 bg-muted/30">
              <Brain className="h-7 w-7 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Improvement Levers
              </h1>
              <p className="text-muted-foreground">{pillar_insight.title}</p>
            </div>
          </div>

          {improvement_levers.length > 0 && (
            <TooltipProvider>
              <div className="flex items-center gap-1 bg-muted p-1 rounded-md">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={view === "cards" ? "default" : "ghost"}
                      size="icon-sm"
                      onClick={() => handleViewChange("cards")}
                      aria-label="Card view"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Box view</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={view === "grid" ? "default" : "ghost"}
                      size="icon-sm"
                      onClick={() => handleViewChange("grid")}
                      aria-label="Grid view"
                    >
                      <Grid3x3 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Matrix view</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={view === "roadmap" ? "default" : "ghost"}
                      size="icon-sm"
                      onClick={() => handleViewChange("roadmap")}
                      aria-label="Roadmap view"
                    >
                      <Calendar className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Timeline view</TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          )}
        </div>

        {improvement_levers.length > 0 ? (
          <>
            {view === "cards" && (
              <CardView
                improvementLevers={improvement_levers}
                onSelect={handleSelect}
              />
            )}
            {view === "grid" && (
              <GridView
                improvementLevers={improvement_levers}
                onSelect={handleSelect}
              />
            )}
            {view === "roadmap" && (
              <RoadmapView
                improvementLevers={improvement_levers}
                onSelect={handleSelect}
              />
            )}
          </>
        ) : (
          <EmptyMessage />
        )}
      </div>

      <ImprovementLeverSheet
        lever={selectedLever}
        pillarInsightId={pillar_insight.id}
        onClose={handleClose}
      />
    </BizminerLayout>
  );
}
